import {
  encodeRelayNotice,
  endedHop,
  relayAbandonedRun,
  newRelayState,
  observeFrame,
  concatBytes,
  planReattach,
  reattachBackoffMs,
  splitCompleteFrames,
  SseFrameScanner,
  startedHop,
  type ReattachLimits,
  type RelayNotice,
  type RelayState,
  type RelayStopReason,
} from "@/features/intelligence/api/frames";

export type RelayOpen =
  | { ok: true; body: ReadableStream<Uint8Array> }
  | { ok: false; status: number; code: string | null };

interface RelayOpeners {
  reattach(runId: string, lastEventId: string | null, signal: AbortSignal): Promise<RelayOpen>;
}

interface RelayHooks {
  onEnd?(summary: RelaySummary): void;
  onRunHandle?(runId: string, threadId: string | null): void;
}

export interface RelaySummary {
  reason: RelayStopReason;
  detail: string;
  runId: string | null;
  frames: number;
  replays: number;
  hops: number;
  elapsedMs: number;
  notice: RelayNotice;
}

export interface RelayOptions {
  limits: ReattachLimits;
  openers: RelayOpeners;
  resume?: { runId?: string | null; lastEventId?: string | null } | null;
  signal?: AbortSignal;
  hooks?: RelayHooks;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

export function relayIntelligenceStream(
  first: ReadableStream<Uint8Array>,
  options: RelayOptions,
): ReadableStream<Uint8Array> {
  const { limits, openers, signal, hooks, resume } = options;
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  const state: RelayState = newRelayState(resume);
  const scanner = new SseFrameScanner();
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();
  const startedAt = now();

  let current: ReadableStream<Uint8Array> | null = first;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  let announcedHandle = false;
  let fragment: Uint8Array = new Uint8Array(0);
  let droppedFragment = false;

  if (state.runId) {
    announcedHandle = true;
    hooks?.onRunHandle?.(state.runId, state.threadId);
  }

  const finish = (
    controller: ReadableStreamDefaultController<Uint8Array>,
    reason: RelayStopReason,
    detail: string,
    extra: { status?: number; code?: string | null } = {},
  ): void => {
    const notice: RelayNotice = {
      reason,
      detail,
      runId: state.runId,
      lastEventId: state.lastEventId,
      ...(droppedFragment ? { truncatedTail: true } : {}),
      ...extra,
    };
    if (reason !== "client-gone") {
      try {
        controller.enqueue(encoder.encode(encodeRelayNotice(notice)));
      } catch {}
    }
    hooks?.onEnd?.({
      reason: notice.reason,
      detail: notice.detail,
      runId: notice.runId,
      frames: state.frames,
      replays: state.replays,
      hops: state.hops,
      elapsedMs: now() - startedAt,
      notice,
    });
    try {
      controller.close();
    } catch {}
  };

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      for (;;) {
        if (signal?.aborted) {
          await release(reader, current);
          finish(controller, "client-gone", "the client disconnected");
          return;
        }
        if (current === null) return;

        if (reader === null) reader = current.getReader();

        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch (err) {
          void err;
          chunk = { done: true, value: undefined };
        }

        if (!chunk.done && chunk.value) {
          const { complete, pending } = splitCompleteFrames(concatBytes(fragment, chunk.value));
          fragment = pending;
          if (complete.length === 0) continue;

          controller.enqueue(complete);
          for (const frame of scanner.push(decoder.decode(complete, { stream: true }))) {
            observeFrame(state, frame);
          }
          if (!announcedHandle && state.runId) {
            announcedHandle = true;
            hooks?.onRunHandle?.(state.runId, state.threadId);
          }
          return;
        }

        await release(reader, null);
        reader = null;
        current = null;
        if (fragment.length > 0) {
          droppedFragment = true;
          fragment = new Uint8Array(0);
        }
        endedHop(state);

        const plan = planReattach(state, limits, now() - startedAt);
        if (plan.kind === "stop") {
          finish(controller, plan.reason, plan.detail);
          return;
        }

        const backoffMs = reattachBackoffMs(state.emptyHops);
        if (backoffMs > 0) {
          await sleep(backoffMs);
          if (signal?.aborted) {
            finish(controller, "client-gone", "the client disconnected");
            return;
          }
        }

        const opened = await openers
          .reattach(plan.runId, plan.lastEventId, signalOf(signal))
          .catch((): RelayOpen => ({ ok: false, status: 0, code: "reattach_unreachable" }));
        if (!opened.ok) {
          finish(
            controller,
            "reattach-failed",
            `the re-attach was refused (status ${opened.status}) — re-read the ` +
              `conversation for the turn's final state`,
            { status: opened.status, code: opened.code },
          );
          return;
        }
        current = opened.body;
        startedHop(state);
      }
    },
    async cancel() {
      await release(reader, current);
      reader = null;
      current = null;
    },
  });
}

function signalOf(signal: AbortSignal | undefined): AbortSignal {
  return signal ?? new AbortController().signal;
}

async function release(
  reader: ReadableStreamDefaultReader<Uint8Array> | null,
  stream: ReadableStream<Uint8Array> | null,
): Promise<void> {
  if (reader) {
    try {
      await reader.cancel();
    } catch {}
    try {
      reader.releaseLock();
    } catch {}
  }
  if (stream) {
    try {
      await stream.cancel();
    } catch {}
  }
}

export interface RelayStopLog {
  level: "warn" | "info";
  line: string;
}

export function relayStopLog(
  op: string,
  conversationId: string,
  summary: RelaySummary,
): RelayStopLog {
  const notice = summary.notice;
  const line =
    `[simon] intelligence relay stop op=${op} reason=${notice.reason} ` +
    `conversation=${conversationId} run=${notice.runId ?? "none"} ` +
    `frames=${summary.frames} replays=${summary.replays} hops=${summary.hops} ` +
    `elapsed=${Math.round(summary.elapsedMs / 1000)}s ` +
    `lastEventId=${notice.lastEventId ?? "none"}` +
    (notice.status === undefined ? "" : ` status=${notice.status}`) +
    (notice.code === undefined || notice.code === null ? "" : ` code=${notice.code}`) +
    (notice.truncatedTail ? " truncatedTail=true" : "") +
    ` detail=${JSON.stringify(notice.detail)}`;
  return { level: relayAbandonedRun(notice.reason) ? "warn" : "info", line };
}
